import { Controller, type Control, type FieldPath, type FieldValues } from "react-hook-form";

import { Field, FieldError, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";

interface EmailFieldProps<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>;
  name: FieldPath<TFieldValues>;
  label: string;
  id?: string;
  required?: boolean;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

export const EmailField = <TFieldValues extends FieldValues>({
  control,
  name,
  label,
  id,
  required = false,
  placeholder,
  disabled,
  className,
}: EmailFieldProps<TFieldValues>) => {
  const fieldId = id ?? name;
  const errorId = `${fieldId}-error`;

  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <Field className={className}>
          <FieldLabel htmlFor={fieldId} className="font-semibold text-foreground">
            {label}
            {required && (
              <span aria-hidden="true" className="ml-0.5 text-destructive">
                *
              </span>
            )}
          </FieldLabel>
          <Input
            {...field}
            value={field.value ?? ""}
            id={fieldId}
            type="email"
            autoComplete="email"
            inputMode="email"
            spellCheck={false}
            autoCapitalize="none"
            placeholder={placeholder}
            disabled={disabled}
            aria-invalid={!!fieldState.error}
            aria-describedby={fieldState.error ? errorId : undefined}
            // beats the legacy WFP border reset, which the [data-slot] rule in tailwind.css targets
            className="border-border"
          />
          <FieldError id={errorId}>{fieldState.error?.message}</FieldError>
        </Field>
      )}
    />
  );
};
